import {
  formatClock,
  normalizeESWTPayload,
  resolveESWTInterpretation,
  summarizeESWTResult,
  type ESWTResultPayload,
} from '../shuttle-walk/shuttleWalkDomain';
import { formatBorgValue } from '../step-tests/stepTestsDomain';

export const getESWTSeverityLabel = (payload: unknown): string => summarizeESWTResult(payload).severity;

export const getESWTSeverityTone = (payload: unknown): string => {
  const summary = summarizeESWTResult(payload);
  if (summary.severity === 'Atenção de segurança') return 'bg-rose-100 text-rose-700';
  if (summary.severity === 'Tempo abaixo da faixa alvo') return 'bg-amber-100 text-amber-700';
  if (summary.severity === 'Faixa alvo 3-8 min') return 'bg-emerald-100 text-emerald-700';
  return 'bg-sky-100 text-sky-700';
};

export const getESWTScoreColor = (payload: unknown): string => {
  const severity = summarizeESWTResult(payload).severity;
  if (severity === 'Atenção de segurança') return '#dc2626';
  if (severity === 'Tempo abaixo da faixa alvo') return '#f59e0b';
  if (severity === 'Faixa alvo 3-8 min') return '#10b981';
  return '#2e5a88';
};

interface ESWTResultRendererProps {
  payload: ESWTResultPayload | null | undefined;
}

const SummaryMetric = ({ label, value, note }: { label: string; value: string; note?: string | null }) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-3xl font-black text-slate-900">{value}</div>
    {note ? <div className="mt-2 text-sm text-slate-500">{note}</div> : null}
  </div>
);

export default function ESWTResultRenderer({ payload }: ESWTResultRendererProps) {
  const normalized = normalizeESWTPayload(payload);
  const summary = summarizeESWTResult(payload);
  const interpretation = resolveESWTInterpretation(payload);
  const scoreColor = getESWTScoreColor(payload);

  if (!normalized) {
    return <div className="rounded-[24px] border border-slate-100 bg-white p-6 text-sm text-slate-600">Sem payload válido para o ESWT.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary-700">Endurance shuttle walk test</p>
          <div className="text-6xl font-black leading-none" style={{ color: scoreColor }}>{formatClock(summary.durationSeconds)}</div>
          <div className="mt-2 text-sm font-semibold text-slate-700">tempo tolerado</div>
          <div className="mt-5 inline-flex items-center gap-2 text-sm font-bold" style={{ color: scoreColor }}>
            <span className="h-2 w-2 rounded-full bg-current" aria-hidden="true" />
            {summary.severity}
          </div>
          <div className="mt-3 text-xs font-medium text-slate-500">{summary.distanceMeters} m estimados</div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Interpretação</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{interpretation}</p>
          </div>
          <div className="grid gap-3 text-sm text-slate-600 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Velocidade alvo: <span className="font-semibold text-slate-900">{normalized.protocol.targetSpeedKmh.toFixed(2)} km/h</span>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Referência ISWT: <span className="font-semibold text-slate-900">{normalized.protocol.sourceIswtLevel != null ? `Nível ${normalized.protocol.sourceIswtLevel}` : 'Manual'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-5">
        <SummaryMetric label="Tempo tolerado" value={formatClock(summary.durationSeconds)} note={summary.withinRecommendedWindow == null ? 'Sem faixa alvo' : summary.withinRecommendedWindow ? 'Entre 3 e 8 min' : 'Fora da faixa 3-8 min'} />
        <SummaryMetric label="Distância" value={`${summary.distanceMeters} m`} note={`${normalized.totalShuttles} shuttle(s)`} />
        <SummaryMetric label="Velocidade alvo" value={`${summary.speedKmh.toFixed(2)}`} note="km/h" />
        <SummaryMetric label="SpO2 mínima" value={summary.minSpo2 != null ? `${summary.minSpo2.toFixed(0)}%` : '—'} note={summary.peakHr != null ? `FC pico ${summary.peakHr.toFixed(0)} bpm` : null} />
        <SummaryMetric label="% do ISWT" value={`${summary.speedPercentOfIswt}%`} note={normalized.protocol.sourceIswtSpeedKmh != null ? `${normalized.protocol.sourceIswtSpeedKmh.toFixed(2)} km/h no ISWT` : 'Sem referência salva'} />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Protocolo registrado</div>
          <div className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="flex items-center justify-between gap-4">
              <span>Pista</span>
              <span className="font-semibold text-slate-900">{normalized.protocol.courseLengthMeters} m</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Velocidade do ISWT</span>
              <span className="font-semibold text-slate-900">{normalized.protocol.sourceIswtSpeedKmh != null ? `${normalized.protocol.sourceIswtSpeedKmh.toFixed(2)} km/h` : '—'}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>% da velocidade pico</span>
              <span className="font-semibold text-slate-900">{normalized.protocol.speedPercentOfIswt}%</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Intervalo entre shuttles</span>
              <span className="font-semibold text-slate-900">{normalized.protocol.shuttleIntervalSeconds.toFixed(2)} s</span>
            </div>
          </div>
        </div>

        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Sinais vitais e Borg</div>
          <div className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="flex items-center justify-between gap-4">
              <span>FC</span>
              <span className="font-semibold text-slate-900">{normalized.baseline.hr ?? '—'} → {normalized.final.hr ?? '—'} bpm</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>SpO2</span>
              <span className="font-semibold text-slate-900">{normalized.baseline.spo2 ?? '—'} → {normalized.final.spo2 ?? '—'}%</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>PA</span>
              <span className="font-semibold text-slate-900">
                {normalized.baseline.bp.sys ?? '—'}/{normalized.baseline.bp.dia ?? '—'} → {normalized.final.bp.sys ?? '—'}/{normalized.final.bp.dia ?? '—'}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Borg dispneia</span>
              <span className="font-semibold text-slate-900">{formatBorgValue(normalized.baseline.borg.dyspnea)} → {formatBorgValue(normalized.final.borg.dyspnea)}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Borg MMII</span>
              <span className="font-semibold text-slate-900">{formatBorgValue(normalized.baseline.borg.legFatigue)} → {formatBorgValue(normalized.final.borg.legFatigue)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
